import React, { PropsWithChildren, ReactNode } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Image, { StaticImageData } from 'next/image';

import { ArrowRight } from '@/blocks/components/Icons';
import { UrgentPicture } from '@/blocks/components/UrgentPicture';
import { CardsGrid } from '@/blocks/components/layout/CardsGrid';
import { FormatDate } from '@/blocks/formatters/format-date';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { WithClassName } from '../types';
import styles from './styles.module.scss';

export type PostType = {
  url?: string;
  title: ReactNode;
  description: ReactNode;
  image: StaticImageData;
  date?: string;
  language?: string;
};

export type PostCardProps = {
  post: PostType;
} & WithClassName;

export const BigPostCard = ({ post, className }: PostCardProps) => {
  const tr = useTranslations();

  const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
    href: post.url,
    target: '_blank',
    rel: 'noopener noreferrer author',
    title: post?.title?.toString(),
    // @ts-ignore
    'data-event-label': post?.title?.toString(),
  };

  return (
    <article className={classNames(styles.card, className)}>
      <div className={styles.wrapper}>
        <div className={classNames(styles.content)}>
          <header>
            {post.date ? (
              <time dateTime={post.date} className={classNames(typography.bodyM)}>
                <FormatDate date={post.date} />
              </time>
            ) : null}
            <a className={classNames(styles.title, typography.h4)} {...linkProps}>
              <h2>{post.title}</h2>
            </a>
          </header>
          <PostSource postUrl={post.url} />
          <p className={classNames(styles.description, typography.subheadingM)}>{post.description}</p>
          {post.url && (
            <a
              {...linkProps}
              className={classNames(
                styles.readMoreLink,
                buttons.btn,
                buttons.btnLink,
                buttons.btnM,
                buttons.btnIconRight,
              )}
            >
              {tr.rich('pages.pressAndMedia.sections.lastPost.readMore')}
              <ArrowRight />
            </a>
          )}
        </div>
        <a {...linkProps} className={styles.thumbnail}>
          <UrgentPicture
            src={post.image}
            alt={post?.title?.toString() || ''}
            width={'400'}
            height={'310'}
            sizes="(max-width: 768px) 100vw, 30vw"
          />
        </a>
      </div>
    </article>
  );
};

export function PostSource({ postUrl }: { postUrl: PostType['url'] }) {
  if (typeof postUrl !== 'string' || !postUrl.length) {
    return null;
  }
  let hostname = postUrl;
  try {
    hostname = new URL(postUrl as string).hostname;
  } catch (e) {
    return null;
  }
  return <address className={classNames(styles.postSource)}>{hostname}</address>;
}

export const PostCard = ({ post, className }: PostCardProps) => {
  return (
    <a
      href={post.url}
      target={'_blank'}
      rel={'noopener noreferrer author'}
      data-event-label={post.title || ''}
      className={styles.postWrapper}
    >
      <article className={classNames(styles.post, className)}>
        <Image
          className={styles.thumbnail}
          src={post.image}
          alt={post?.title?.toString() || ''}
          width={'360'}
          height={'222'}
          sizes="(max-width: 768px) 100vw, (max-width: 992px) 50vw, 30vw"
        />
        <div className={styles.content}>
          <header>
            {post.date ? (
              <time dateTime={post.date} className={classNames(typography.bodyS, styles.date)}>
                <FormatDate date={post.date} />
              </time>
            ) : null}
            <h3
              className={classNames(styles.title, typography.subheadingML)}
              data-event-label={post.title || ''}
            >
              {post.title || ''}
            </h3>
            <PostSource postUrl={post.url} />
          </header>
          <p className={classNames(styles.description, typography.bodyS)}>{post.description || ''}</p>
        </div>
      </article>
    </a>
  );
};

export const PostsGrid = ({ children }: PropsWithChildren) => {
  return (
    <CardsGrid columns={{ md: 2, lg: 3 }} className={styles.postsGrid} tag={'ul'}>
      {React.Children.map(children, (c) => (
        <li>{c}</li>
      ))}
    </CardsGrid>
  );
};
